import {AmountDisplay, Button, Icon} from '@sollapay/ui/components';
import {useDisclosure} from '@sollapay/ui/hooks';
import {BUSINESS_TIME_ZONE, formatDayMonthInTimeZone} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {BreakdownRow} from './BreakdownRow';
import {BreakdownSection} from './BreakdownSection';
import {useCountedLabel} from './useCountedLabel';

import type {BreakdownRowProps} from './BreakdownRow';
import type {BalanceBreakdown, BreakdownBucket} from '@/features/trust-accounts/view/utils';
import type {Money} from '@sollapay/utils';

export interface OutgoingSectionProps {
  outgoing: BalanceBreakdown['outgoing'];
}

export function OutgoingSection({outgoing}: OutgoingSectionProps) {
  const {t} = useTranslation('trustAccounts');
  const countedLabel = useCountedLabel();
  const {isOpen, toggle} = useDisclosure();

  return (
    <BreakdownSection label={t('view.balanceMetric.breakdown.outgoing')}>
      <Button
        variant="tertiary"
        className="h-auto w-full p-0 hover:bg-transparent"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <BreakdownRow
          icon="arrow-up"
          tone="warning"
          title={countedLabel('outgoingTotal', outgoing.total.count)}
          subtitle={t('view.balanceMetric.breakdown.outgoingTotalHint')}
          amount={
            <div className="flex items-center gap-1.5">
              <OutgoingAmount amount={outgoing.total.amount} />
              <Icon
                name="chevron-down"
                className="size-4 text-fg-tertiary transition-transform group-aria-expanded/button:rotate-180"
              />
            </div>
          }
        />
      </Button>
      {isOpen && <OutgoingDetails outgoing={outgoing} />}
    </BreakdownSection>
  );
}

type DetailRow = {
  key: 'sentToBank' | 'approved' | 'awaitingReview';
  tone: BreakdownRowProps['tone'];
  bucket: BreakdownBucket;
  subtitle: string;
};

function OutgoingDetails({outgoing}: OutgoingSectionProps) {
  const {t} = useTranslation('trustAccounts');
  const countedLabel = useCountedLabel();
  const {sentToBank, approved, awaitingReview} = outgoing;

  const rows: DetailRow[] = [
    {
      key: 'sentToBank',
      tone: 'warning',
      bucket: sentToBank,
      subtitle: sentToBank.sentAt
        ? t('view.balanceMetric.breakdown.sentToBankHintDated', {
            date: formatDayMonthInTimeZone(new Date(sentToBank.sentAt), BUSINESS_TIME_ZONE)
          })
        : t('view.balanceMetric.breakdown.sentToBankHint')
    },
    {
      key: 'approved',
      tone: 'reserved',
      bucket: approved,
      subtitle: t('view.balanceMetric.breakdown.approvedHint')
    },
    {
      key: 'awaitingReview',
      tone: 'pending',
      bucket: awaitingReview,
      subtitle: t('view.balanceMetric.breakdown.awaitingReviewHint')
    }
  ];

  return (
    <div
      data-slot="balance-breakdown-outgoing-details"
      className="flex flex-col gap-3 rounded-lg border border-border-subtle p-3"
    >
      {rows
        .filter(row => !row.bucket.amount.isZero())
        .map(row => (
          <BreakdownRow
            key={row.key}
            icon="arrow-up"
            tone={row.tone}
            title={countedLabel(row.key, row.bucket.count)}
            subtitle={row.subtitle}
            amount={<OutgoingAmount amount={row.bucket.amount} />}
          />
        ))}
    </div>
  );
}

interface OutgoingAmountProps {
  amount: Money;
}

/** Outgoing money that hasn't cleared is a pending state, not a loss — warning, not destructive. */
function OutgoingAmount({amount}: OutgoingAmountProps) {
  return (
    <AmountDisplay
      amount={amount.toString()}
      variant="debit"
      size="sm"
      className="text-text-warning tabular-nums"
    />
  );
}
